import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Table, Empty } from 'antd';
import { useQuery } from "@apollo/client";
import SkeletonCustom from "components/skeleton";
import { useDebounce } from "components/use-hooks";
import { Localize } from "components/service";

const SkeletonData = SkeletonCustom.TableData;


const TableMain = ({
    querySkip = false,
    model,
    query,
    currentObj,
    routeUrl,
    routeSearch,
    currentPage,
    searchText,
    objFilter,
    tableHelper,
    children,
    varSet,
    extraClass = "",
    tableVisible = true,
    externalModel,
    externalLoading = false,
    scroll,
    sortingName = false,
    resetSorting = false,
    setVariables = () => { },
    onData = () => { },
    onSortChange,
    objOrderBy = [ { column: "ID", order: "DESC" } ],
    tableLayout = 'fixed',
}) => {


    const history = useHistory();
    const preserveRouteSearch = routeSearch !== undefined;
    const perPage = varSet?.perPage ?? 10;
    const [ orderBy, setOrderBy ] = useState(objOrderBy);


    const variables = {
        text: useDebounce(searchText),
        first: perPage,
        page: currentPage,
        where: objFilter,
        orderBy,
        ...varSet ?? undefined
    };


    const hasExternalModel = externalModel !== undefined && externalModel !== null;

    let { data, loading } = useQuery(query, {
        skip: querySkip || hasExternalModel,
        variables: { ...variables },
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first'
    });


    const resolvedModel = hasExternalModel ? externalModel : data?.[ model ] ?? {};
    const resolvedLoading = hasExternalModel ? externalLoading : loading;
    const { paginatorInfo } = resolvedModel;
    const variablesKey = JSON.stringify(variables);
    const [ synchronizedVariablesKey, setSynchronizedVariablesKey ] = useState(variablesKey);
    const waitingForQueryVariables = !hasExternalModel && synchronizedVariablesKey !== variablesKey;

    /*eslint-disable */
    useEffect(() => {
        // Apollo 3.5 applies new options in its effect; this render can still hold the old result.
        if (synchronizedVariablesKey !== variablesKey)
        {
            setSynchronizedVariablesKey(variablesKey);
            if (!hasExternalModel) return;
        }
        setVariables(variables);
        onData(resolvedModel.data || []);
    }, [ data, externalModel, loading, variablesKey, synchronizedVariablesKey ]);

    useEffect(() => {
        if (resetSorting) setOrderBy(resetSorting);
    }, [ resetSorting ]);
    /*eslint-enable */

    useEffect(() => {

        if (searchText !== '' && searchText !== undefined)
        {
            if (!preserveRouteSearch)
            {
                history.push(routeUrl);
            }
            else if (history.location.pathname !== routeUrl)
            {
                history.push({ pathname: routeUrl, search: history.location.search });
            }
        }
    }, [ searchText, history, routeUrl, preserveRouteSearch ]);


    const dataSource = resolvedLoading || waitingForQueryVariables ?
        SkeletonData(tableHelper.columns, perPage, 'my-2') :
        tableHelper.data(resolvedModel.data, currentObj, variables);


    return (
        <>

            { children }

            { tableVisible &&

                <Table
                    className={ `table-main ${ extraClass }` }
                    locale={ {
                        emptyText: (
                            <div className="no-data-box">
                                <Empty image={ Empty.PRESENTED_IMAGE_SIMPLE } />
                                <span className="no-data-text"><Localize>GLOBAL.Text_NoData</Localize></span>
                            </div>
                        ),
                        triggerDesc: <Localize>TABLES.Column_Sorting_Tooltip_Descending</Localize>,
                        triggerAsc: <Localize>TABLES.Column_Sorting_Tooltip_Ascending</Localize>,
                        cancelSort: <Localize>TABLES.Column_Sorting_Tooltip_Cancel</Localize>
                    } }
                    dataSource={ dataSource }
                    columns={ tableHelper.columns }
                    tableLayout={ tableLayout }
                    scroll={ scroll }
                    size="middle"
                    pagination={ {
                        // pageSizeOptions: [ "10", "20" ],
                        showSizeChanger: false,
                        position: [ "bottomCenter" ],
                        pageSize: perPage,
                        total: paginatorInfo?.total,
                        current: currentPage,
                        hideOnSinglePage: !(paginatorInfo?.total / paginatorInfo?.perPage > 1),
                        onChange: (page) => {
                            let path = page === 1 ? `${ routeUrl }` : `${ routeUrl }/page/${ page }`
                            history.push(routeSearch === undefined ? path : { pathname: path, search: routeSearch });
                        }

                    } }
                    onChange={ (pagination, filters, sorter, extra) => {

                        if (onSortChange)
                        {
                            if (extra?.action === 'sort') onSortChange(sorter);
                            return;
                        }

                        const { column, order } = sorter;

                        if (column && order)
                        {

                            const orderBy = [ { column: column.columnIndex, order: order === "ascend" ? "ASC" : "DESC" } ];

                            setOrderBy(orderBy)
                            sortingName && localStorage.setItem(sortingName, JSON.stringify(orderBy));

                        }
                    } }
                />
            }

        </>
    );
};

export default TableMain;
